import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { get } from "../lib/api";
import { useDocumentTitle, useLoad } from "../lib/hooks";
import { formatDay, humanize, optionLabel } from "../lib/format";
import {
    DASH,
    FUNNEL_COUNTS,
    LEAD_SOURCES,
    RATIOS,
    formatEUR,
    formatNumber,
    formatRatio,
    previousQuarter,
    quarterOf,
    quarterText,
    rangeText,
} from "../lib/reports";
import { todayISO } from "../../helpers/datetime";
import { Button, Card, Empty, ErrorBox, Input, Loading, PageHead, Segment } from "../ui";
import { BarList, ColumnChart } from "../components/Charts";
import TargetCard from "../components/TargetCard";

const DEFINITION_ORDER = [
    "period",
    "touches",
    "introductions",
    "first_meetings",
    "proposals",
    "wins",
    "contracted_value_eur",
    "touch_to_introduction",
    "meeting_to_proposal",
    "proposal_to_win",
    "meetings_per_win",
    "average_deal_size_eur",
    "value_per_meeting_eur",
    "wins_by_source",
    "stage_days",
    "ratios",
];

const DEFINITION_LABEL = {
    period: "Period",
    ratios: "Ratios",
    first_meetings: "First meetings",
    contracted_value_eur: "Contracted value",
    wins_by_source: "Wins by source",
    stage_days: "Days per stage",
};

const defLabel = (k) =>
    DEFINITION_LABEL[k] || (FUNNEL_COUNTS.find((c) => c.key === k) || RATIOS.find((r) => r.key === k) || { label: humanize(k) }).label;

function Definitions({ definitions }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDoc);
        window.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);
    const keys = [...DEFINITION_ORDER.filter((k) => definitions[k]), ...Object.keys(definitions).filter((k) => !DEFINITION_ORDER.includes(k))];
    return (
        <div className="hz-menu-wrap" ref={ref}>
            <Button variant="outline" icon="ri-information-line" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
                Definitions
            </Button>
            {open && (
                <div className="hz-popover" role="dialog" aria-label="Metric definitions">
                    <div className="hz-popover-title">How the numbers are counted</div>
                    <dl className="hz-deflist">
                        {keys.map((k) => (
                            <React.Fragment key={k}>
                                <dt>{defLabel(k)}</dt>
                                <dd>{definitions[k]}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </div>
            )}
        </div>
    );
}

function useRange() {
    const location = useLocation();
    const history = useHistory();
    const q = new URLSearchParams(location.search);
    const cur = quarterOf(todayISO());
    const from = q.get("from") || cur.from;
    const to = q.get("to") || cur.to;
    const group = q.get("group") || "week";
    const set = (next) => {
        const v = { from, to, group, ...next };
        const p = new URLSearchParams();
        if (v.from !== cur.from || v.to !== cur.to) {
            p.set("from", v.from);
            p.set("to", v.to);
        }
        if (v.group !== "week") p.set("group", v.group);
        history.replace({ search: p.toString() ? `?${p}` : "" });
    };
    return { from, to, group, set };
}

const CHART_METRICS = [
    { value: "touches", label: "Touches" },
    { value: "introductions", label: "Intros" },
    { value: "first_meetings", label: "Meetings" },
    { value: "proposals", label: "Proposals" },
    { value: "wins", label: "Wins" },
    { value: "contracted_value_eur", label: "Value" },
];

const shortBucket = (label) => {
    let m = /^\d{4}-W(\d{2})$/.exec(label);
    if (m) return `W${+m[1]}`;
    m = /^(\d{4})Q([1-4])$/.exec(label);
    if (m) return `Q${m[2]} ${m[1].slice(2)}`;
    return label;
};

export default function Funnel() {
    useDocumentTitle("Funnel");
    const { from, to, group, set } = useRange();
    const [metric, setMetric] = useState("touches");
    const [draft, setDraft] = useState({ from, to });
    useEffect(() => setDraft({ from, to }), [from, to]);

    const valid = from && to && from <= to;
    const funnel = useLoad(() => (valid ? get("v1/crm/reports/funnel", { from, to, group_by: group }) : Promise.resolve(null)), [from, to, group]);
    const targetQuarter = quarterOf(to && to < todayISO() ? to : todayISO() < from ? from : todayISO());
    const target = useLoad(() => get("v1/crm/reports/target", { quarter: targetQuarter && targetQuarter.label }), [targetQuarter && targetQuarter.label]);

    const today = todayISO();
    const thisQ = quarterOf(today);
    const lastQ = previousQuarter(today);
    const presets = [
        { label: "This quarter", from: thisQ.from, to: thisQ.to },
        { label: "Last quarter", from: lastQ.from, to: lastQ.to },
        { label: "Year to date", from: `${today.slice(0, 4)}-01-01`, to: today },
    ];

    const data = funnel.data;
    const total = data && data.total;
    const counts = (total && total.counts) || {};
    const ratios = (total && total.ratios) || {};
    const buckets = (data && data.buckets) || [];
    const metricDef = FUNNEL_COUNTS.find((c) => c.key === metric);
    const fmtMetric = (v) => (metricDef && metricDef.eur ? formatEUR(v, { compact: true }) : formatNumber(v, 0));

    const applyDraft = () => {
        if (draft.from && draft.to && draft.from <= draft.to) set({ from: draft.from, to: draft.to });
    };

    return (
        <>
            <PageHead
                crumb="Reports"
                title="Funnel"
                sub={`${rangeText(from, to)} · counts are events dated in the period`}
                actions={data && data.definitions ? <Definitions definitions={data.definitions} /> : null}
            />
            <Card noBody style={{ marginBottom: "var(--hz-gutter)" }}>
                <div className="hz-filterbar" style={{ borderBottom: 0 }}>
                    {presets.map((p) => (
                        <button
                            key={p.label}
                            type="button"
                            className={"hz-chip" + (p.from === from && p.to === to ? " active" : "")}
                            onClick={() => set({ from: p.from, to: p.to })}
                        >
                            {p.label}
                        </button>
                    ))}
                    <span className="hz-row" style={{ gap: 6 }}>
                        <Input small type="date" aria-label="From" value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} onBlur={applyDraft} style={{ width: 140 }} />
                        <span className="hz-muted">to</span>
                        <Input small type="date" aria-label="To" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} onBlur={applyDraft} style={{ width: 140 }} />
                    </span>
                    <span className="hz-spacer"></span>
                    <span className="hz-small hz-muted">Group by</span>
                    <Segment
                        value={group}
                        onChange={(g) => set({ group: g })}
                        options={[
                            { value: "week", label: "Week" },
                            { value: "quarter", label: "Quarter" },
                        ]}
                    />
                </div>
                {draft.from && draft.to && draft.from > draft.to && <div className="hz-alert hz-alert-danger" style={{ margin: "0 16px 12px" }}>The start date is after the end date.</div>}
            </Card>

            {funnel.error && <ErrorBox error={funnel.error} onRetry={funnel.reload} />}
            {funnel.loading && !data && <Card><Loading /></Card>}
            {data && (
                <div className="hz-stack">
                    {(data.notes || []).length > 0 && (
                        <div className="hz-alert hz-alert-warning">
                            {data.notes.map((n, i) => (
                                <div key={i}><i className="ri-information-line"></i> {n}</div>
                            ))}
                        </div>
                    )}
                    <div className="hz-grid-2">
                        <Card title="Counts">
                            <div className="hz-tiles">
                                {FUNNEL_COUNTS.map((c) => (
                                    <div key={c.key} className="hz-tile">
                                        <div className="hz-stat">{c.eur ? formatEUR(counts[c.key]) : formatNumber(counts[c.key], 0)}</div>
                                        <div className="hz-stat-label">{c.label}</div>
                                        {c.key === "introductions" && (
                                            <div className="hz-xs hz-muted">
                                                {counts.intro_received || 0} received · {counts.intro_made || 0} made · {counts.introduction_leads || 0} from leads
                                            </div>
                                        )}
                                        {c.key === "wins" && <div className="hz-xs hz-muted">{counts.losses || 0} lost</div>}
                                        {c.key === "contracted_value_eur" && counts.wins_without_value > 0 && (
                                            <div className="hz-xs hz-muted">{counts.wins_without_value} wins without a value</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Card>
                        <Card title="Ratios">
                            <div className="hz-tiles">
                                {RATIOS.map((r) => (
                                    <div key={r.key} className="hz-tile">
                                        <div className={"hz-stat" + (ratios[r.key] === null || ratios[r.key] === undefined ? " hz-stat-empty" : "")}>
                                            {formatRatio(ratios[r.key], r.kind)}
                                        </div>
                                        <div className="hz-stat-label">{r.label}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="hz-xs hz-muted" style={{ marginTop: 10 }}>{DASH} means there is nothing to divide by yet.</div>
                        </Card>
                    </div>

                    <TargetCard target={target.data} />

                    <Card
                        noBody
                        title={group === "week" ? "Week by week" : "Quarter by quarter"}
                        actions={<Segment value={metric} onChange={setMetric} options={CHART_METRICS} />}
                    >
                        <div className="hz-card-body">
                            {buckets.length === 0 ? (
                                <Empty small>No periods in this range.</Empty>
                            ) : (
                                <ColumnChart
                                    ariaLabel={`${metricDef.label} per ${group}`}
                                    data={buckets.map((b) => ({
                                        key: b.label,
                                        label: shortBucket(b.label),
                                        value: Number(b.counts[metric]) || 0,
                                        display: fmtMetric(b.counts[metric]),
                                        title: `${b.label} (${rangeText(b.from, b.to)})${b.partial ? ", partial" : ""}: ${fmtMetric(b.counts[metric])}`,
                                    }))}
                                />
                            )}
                        </div>
                        {buckets.length > 0 && (
                            <div className="hz-table-wrap" style={{ borderTop: "1px solid var(--hz-border)" }}>
                                <table className="hz-table hz-table-num">
                                    <thead>
                                        <tr>
                                            <th>{group === "week" ? "Week" : "Quarter"}</th>
                                            <th>Touches</th>
                                            <th>Intros</th>
                                            <th>Meetings</th>
                                            <th>Proposals</th>
                                            <th>Wins</th>
                                            <th>Value</th>
                                            <th>Meeting → proposal</th>
                                            <th>Proposal → win</th>
                                            {group === "quarter" && <th>Target</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {buckets.map((b) => (
                                            <tr key={b.label}>
                                                <td>
                                                    <span className="hz-strong">{group === "quarter" ? quarterText(b.label) : b.label}</span>
                                                    <span className="hz-xs hz-muted"> {formatDay(b.from, { short: true })}–{formatDay(b.to, { short: true })}{b.partial ? " · partial" : ""}</span>
                                                </td>
                                                <td>{b.counts.touches}</td>
                                                <td>{b.counts.introductions}</td>
                                                <td>{b.counts.first_meetings}</td>
                                                <td>{b.counts.proposals}</td>
                                                <td>{b.counts.wins}</td>
                                                <td>{b.counts.contracted_value_eur ? formatEUR(b.counts.contracted_value_eur) : DASH}</td>
                                                <td>{formatRatio(b.ratios.meeting_to_proposal, "percent")}</td>
                                                <td>{formatRatio(b.ratios.proposal_to_win, "percent")}</td>
                                                {group === "quarter" && <td>{b.target_pct === null || b.target_pct === undefined ? DASH : `${formatNumber(b.target_pct, 1)}%`}</td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>

                    <div className="hz-grid-2">
                        <Card title="Wins by source">
                            {(data.wins_by_source || []).every((s) => !s.wins) ? (
                                <Empty small>No wins in this period.</Empty>
                            ) : (
                                <BarList
                                    ariaLabel="Wins by lead source"
                                    rows={(data.wins_by_source || []).map((s) => ({
                                        key: s.source,
                                        label: optionLabel(LEAD_SOURCES, s.source),
                                        value: s.wins,
                                        display: `${s.wins} ${s.wins === 1 ? "win" : "wins"}`,
                                        sub: s.value_eur ? formatEUR(s.value_eur) : null,
                                    }))}
                                />
                            )}
                        </Card>
                        <Card title="Average days per stage" actions={<span className="hz-small hz-muted">Stays that ended in the period</span>}>
                            <BarList
                                ariaLabel="Average days in each lead stage"
                                rows={(data.stage_days || []).map((s) => ({
                                    key: s.stage,
                                    label: humanize(s.stage).replace(/^./, (c) => c.toUpperCase()),
                                    value: s.average_days || 0,
                                    display: s.average_days === null ? DASH : `${formatNumber(s.average_days, 1)} days`,
                                    sub: `${s.stays} ${s.stays === 1 ? "stay" : "stays"}${s.imported ? `, ${s.imported} imported` : ""}`,
                                }))}
                            />
                        </Card>
                    </div>
                </div>
            )}
        </>
    );
}
